import { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@simplea/shared-ui';
import axios from 'axios';
import { Box, Layout } from '../../../components';
import { getBaseUrl } from '../../../utils';
import { ReactComponent as DocumentIcon } from '../../../static/images/icons/document-text.svg';
import InfoBox from '../../../components/InfoBox/InfoBox';
import Loader from '../../MojeId/components/Loader';
import { NotFound } from '../../Errors/NotFound';
import './BankIdSign.styles.css';

interface Bank {
    Id: string;
    Title: string;
    Description: string;
    AvailableLogoImages: Array<{
        Id: string;
        Url: string;
        Width: number;
        Height: number;
    }>;
    // TODO: testing
    _disabled?: boolean;
}

interface Document {
    Name: string;
    Description: string;
    BinaryContent: string;
    ExternalId: string;
}

interface BankIdSignInitiateProps {
    language: string;
}

const BankIdSignInitiate = ({ language }: BankIdSignInitiateProps) => {
    const params = useParams();
    const [banks, setBanks] = useState<Bank[]>([]);
    const [banksLoading, setBanksLoading] = useState(true);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [documentsLoading, setDocumentsLoading] = useState(true);
    const [continueLoading, setContinueLoading] = useState(false);

    useEffect(() => {
        axios
            .get(`${getBaseUrl()}/api/bank-id/get-list-of-capable-banks`)
            .then((response) => response.data)
            .then((result) => {
                if (result.Banks) {
                    setBanks(result.Banks);
                }
            })
            .catch((e) => {
                console.error(e);
            })
            .finally(() => {
                setBanksLoading(false);
            });
    }, []);

    useEffect(() => {
        axios
            .get(`${getBaseUrl()}/api/bank-id/get-list-of-documents-to-sign/${params.approverExternalId}`)
            .then((response) => response.data)
            .then((result) => {
                if (result.Documents) {
                    setDocuments(result.Documents);
                }
            })
            .catch((e) => {
                console.error(e);
            })
            .finally(() => {
                setDocumentsLoading(false);
            });
    }, [params]);

    // useEffect(() => {
    //     axios
    //         .get(
    //             `${getBaseUrl()}/api/bank-id/upload-documents-to-sign/${params.approverExternalId}`
    //         )
    //         .then((response) => response.data)
    //         .then((result) => {
    //             if (result.RedirectUrl) {
    //                 // window.location.replace(result.RedirectUrl);
    //             }
    //         })
    //         .catch((e) => {
    //             console.error(e);
    //         });
    // }, [params]);

    const handleContinue = () => {
        setContinueLoading(true);
        axios
            .get(`${getBaseUrl()}/api/bank-id/upload-documents-to-sign/${params.approverExternalId}`)
            .then((response) => response.data)
            .then((result) => {
                if (result.RedirectUrl) {
                    window.location.replace(result.RedirectUrl);
                    setContinueLoading(false);
                }
            })
            .catch(() => {
                setContinueLoading(false);
            });
    };

    const handlePdfClick = (document: Document) => {
        const pdfData = atob(document.BinaryContent);
        const bytes = new Uint8Array(pdfData.length);
        for (let i = 0; i < pdfData.length; i++) {
            bytes[i] = pdfData.charCodeAt(i);
        }

        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        window.open(url, '_blank');
    };

    if (!documentsLoading && documents.length === 0) {
        return <NotFound language={language} />;
    }

    return (
        <Layout
            headline="Podepsání dokumentů"
            description="Bezpečně podepište své dokumenty pomocí BankID. Proces zabere jen několik minut."
        >
            <div className="bankIdSign-wrapper">
                <Box className="white-box">
                    <div className="white-box__content">
                        {documentsLoading || banksLoading ? (
                            <Loader size="xsmall" className="m-auto" />
                        ) : (
                            <Fragment>
                                <div>
                                    <h3>
                                        <span className="title-num">1</span>Dokumenty k podpisu
                                    </h3>
                                    <div className="documents">
                                        {documents.map((d) => (
                                            <div key={`document-${d.ExternalId}`} className="document">
                                                <div className="document__icon">
                                                    <DocumentIcon />
                                                </div>
                                                <div className="document__content">
                                                    <p>{d.Name}</p>
                                                    <p>{d.Description}</p>
                                                </div>
                                                <div className="document__download">
                                                    <span onClick={() => handlePdfClick(d)}>PDF</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <h3>
                                        <span className="title-num">2</span>Vyberte svou banku
                                    </h3>
                                    <div className="banks">
                                        {banks.map((b) => (
                                            <div className={`bank${b._disabled ? ' bank-disabled' : ''}`}>
                                                <label className="bank__content">
                                                    {b.Title}
                                                    <input type="radio" name="bank" disabled={b._disabled} />
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Fragment>
                        )}
                    </div>
                </Box>
                <div>
                    <Button
                        small
                        disabled={banksLoading || documentsLoading || continueLoading}
                        // loading={continueLoading}
                        onClick={handleContinue}
                    >
                        Pokračovat s BANKID
                    </Button>
                </div>
                <div className="bankIdSign__alerts">
                    <InfoBox
                        variant="info"
                        title="Bezpečný podpis"
                        description="Používáme BankID pro nejvyšší úroveň zabezpečení. Vaše dokumenty budou podepsány kvalifikovaným elektronickým podpisem."
                    />
                    <InfoBox
                        variant="warning"
                        title="Bezpečný podpis"
                        description={
                            <>
                                Kontaktujte nás na <a href="mailto:info@simplea.cz">info@simplea.cz</a> nebo na čísle
                                +420 123 456 789.
                            </>
                        }
                    />
                </div>
            </div>
        </Layout>
    );
};

export default BankIdSignInitiate;
